import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CalendarDays, ArrowRight, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SLOT_REGIONS = ['indonesia', 'maldives', 'vietnam', 'cyprus'];

const Contact = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    region: '',
    message: '',
    consultation_date: '',
    consultation_time: '',
    slot_id: ''
  });

  useEffect(() => {
    document.title = 'Book a Consultation | Fundora Global';
    axios
      .get(`${BACKEND_URL}/api/consultation-slots`)
      .then(({ data }) => setSlots(data))
      .catch((error) => console.error('Could not load consultation dates:', error));
  }, []);

  const regionLabel = useCallback(
    (region) => {
      const labels = {
        indonesia: t('contact.indonesiaFull'),
        maldives: t('contact.maldives'),
        vietnam: t('contact.vietnam'),
        cyprus: t('contact.northernCyprus'),
        multiple: t('contact.multipleRegions')
      };
      return labels[region] || region;
    },
    [t]
  );

  const slotsForRegion = (region) => slots.filter((s) => s.region === region);

  const selectRegion = (region) => {
    setFormData((prev) => ({ ...prev, region, consultation_date: '', consultation_time: '', slot_id: '' }));
    if (SLOT_REGIONS.includes(region)) {
      setDateDialogOpen(true);
    }
  };

  const chooseSlot = (slot) => {
    setFormData((prev) => ({
      ...prev,
      region: slot.region,
      consultation_date: slot.date,
      consultation_time: slot.time,
      slot_id: slot.id
    }));
    setDateDialogOpen(false);
  };

  const registrationPath = (slot) => `/register/${slot.consultation_id}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/consultations`, {
        ...formData,
        language: (i18n.language || 'en').slice(0, 2)
      });
      toast({
        title: t('contact.messageSent'),
        description: t('contact.messageSentDesc'),
      });
      setFormData({ name: '', email: '', phone: '', region: '', message: '', consultation_date: '', consultation_time: '', slot_id: '' });
    } catch (error) {
      console.error('Consultation request error:', error);
      toast({
        title: t('contact.error'),
        description: error.response?.data?.detail || t('contact.errorMessage'),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const upcomingByRegion = SLOT_REGIONS.map((region) => slotsForRegion(region)[0]).filter(Boolean);
  const dialogSlots = slotsForRegion(formData.region);

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#102131] to-[#1a3449] text-[#F7FDFF] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-[#F7FDFF]/80 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Upcoming consultations */}
      {upcomingByRegion.length > 0 && (
        <section className="py-16 bg-[#F7FDFF]" data-testid="upcoming-consultations">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-[#CDA259] mb-3">{t('contact.onlineConsultation')}</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#102131] mb-3">{t('contact.upcomingTitle')}</h2>
              <p className="text-base text-gray-600 max-w-2xl">{t('contact.upcomingSubtitle')}</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {upcomingByRegion.map((slot) => (
                <Link
                  key={slot.id}
                  to={registrationPath(slot)}
                  data-testid={`slot-card-${slot.region}`}
                  className="group text-left bg-white border border-gray-200 rounded-xl p-6 hover:border-[#CDA259] hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <p className="text-sm font-bold tracking-wide text-[#102131] uppercase mb-3">
                    {regionLabel(slot.region)}
                  </p>
                  <div className="flex items-center gap-2 text-[#CDA259] mb-4">
                    <CalendarDays size={16} />
                    <span className="text-sm font-semibold">{slot.date} · {slot.time}</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-600 mb-6">
                    <Video size={16} className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{t('contact.onlineConsultation')}</span>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#102131] group-hover:gap-3 transition-all duration-200">
                    {t('contact.reserve')}
                    <ArrowRight size={16} className="text-[#CDA259]" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Form */}
      <section className="py-20 bg-white" id="consultation-form">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#102131] mb-6">
                {t('contact.getInTouch')}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="consultation-form">
                <div>
                  <Label htmlFor="region" className="text-[#102131]">{t('contact.interestedRegion')} *</Label>
                  <Select value={formData.region} onValueChange={selectRegion} required>
                    <SelectTrigger className="mt-2 border-gray-300" data-testid="region-select-trigger">
                      <SelectValue placeholder={t('contact.selectRegion')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indonesia" data-testid="region-option-indonesia">{t('contact.indonesiaFull')}</SelectItem>
                      <SelectItem value="maldives" data-testid="region-option-maldives">{t('contact.maldives')}</SelectItem>
                      <SelectItem value="vietnam" data-testid="region-option-vietnam">{t('contact.vietnam')}</SelectItem>
                      <SelectItem value="cyprus" data-testid="region-option-cyprus">{t('contact.northernCyprus')}</SelectItem>
                      <SelectItem value="multiple" data-testid="region-option-multiple">{t('contact.multipleRegions')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {SLOT_REGIONS.includes(formData.region) && (
                  <div>
                    <Label className="text-[#102131]">{t('contact.consultationDate')}</Label>
                    <button
                      type="button"
                      data-testid="open-date-dialog-btn"
                      onClick={() => setDateDialogOpen(true)}
                      className="mt-2 w-full flex items-center justify-between rounded-md border border-gray-300 px-3 py-2.5 text-sm hover:border-[#CDA259] transition-colors duration-200"
                    >
                      <span className="flex items-center gap-2 text-[#102131]" data-testid="selected-date-label">
                        <CalendarDays size={16} className="text-[#CDA259]" />
                        {formData.consultation_date
                          ? `${formData.consultation_date} · ${formData.consultation_time}`
                          : dialogSlots.length > 0
                            ? t('contact.selectDate')
                            : t('contact.noDates')}
                      </span>
                      {dialogSlots.length > 0 && (
                        <span className="text-xs font-medium text-[#CDA259]">{t('contact.changeDate')}</span>
                      )}
                    </button>
                  </div>
                )}

                <div>
                  <Label htmlFor="name" className="text-[#102131]">{t('contact.fullName')} *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2 border-gray-300 focus:border-[#CDA259]"
                    placeholder={t('contact.fullNamePlaceholder')}
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#102131]">{t('contact.email')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 border-gray-300 focus:border-[#CDA259]"
                    placeholder={t('contact.emailPlaceholder')}
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#102131]">{t('contact.phone')} *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    data-testid="contact-phone-input"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2 border-gray-300 focus:border-[#CDA259]"
                    placeholder={t('contact.phonePlaceholder')}
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#102131]">{t('contact.message')}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-2 border-gray-300 focus:border-[#CDA259] min-h-32"
                    placeholder={t('contact.messagePlaceholder')}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isLoading}
                  className="w-full bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold py-6 text-lg transition-all duration-200 hover:scale-105"
                >
                  {isLoading ? t('contact.sending') : t('contact.sendMessage')}
                  <Send className="ml-2" size={20} />
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-[#102131] mb-6">
                {t('contact.contactInfo')}
              </h2>
              <p className="text-gray-600 mb-8">
                {t('contact.contactInfoDesc')}
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-4 bg-[#F7FDFF] rounded-lg">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#CDA259]/10 rounded-full flex items-center justify-center">
                    <Mail className="text-[#CDA259]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#102131] mb-1">{t('contact.emailLabel')}</h3>
                    <p className="text-gray-600">info@fundoraglobal.com</p>
                    <p className="text-sm text-gray-500 mt-1">{t('contact.respondTime')}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-[#F7FDFF] rounded-lg">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#CDA259]/10 rounded-full flex items-center justify-center">
                    <Phone className="text-[#CDA259]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#102131] mb-1">{t('contact.phoneLabel')}</h3>
                    <p className="text-gray-600">+370 602 22526</p>
                    <p className="text-sm text-gray-500 mt-1">{t('contact.phoneHours')}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-[#F7FDFF] rounded-lg">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#CDA259]/10 rounded-full flex items-center justify-center">
                    <MapPin className="text-[#CDA259]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#102131] mb-1">{t('contact.office')}</h3>
                    <p className="text-gray-600">Žalgirio g. 93, LT-08218</p>
                    <p className="text-sm text-gray-500 mt-1">Vilnius, Lithuania</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Date picker dialog */}
      <Dialog open={dateDialogOpen} onOpenChange={setDateDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="date-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#102131]">{t('contact.pickDate')}</DialogTitle>
            <DialogDescription>
              {formData.region ? `${regionLabel(formData.region)} — ${t('contact.pickDateDesc')}` : t('contact.pickDateDesc')}
            </DialogDescription>
          </DialogHeader>

          {dialogSlots.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {dialogSlots.map((slot) => {
                const selected = slot.id === formData.slot_id;
                return (
                  <button
                    key={slot.id}
                    type="button"
                    data-testid={`slot-option-${slot.id}`}
                    onClick={() => chooseSlot(slot)}
                    className={`w-full flex items-center justify-between rounded-lg border px-4 py-3 text-left transition-all duration-200 ${
                      selected ? 'border-[#CDA259] bg-[#CDA259]/10' : 'border-gray-200 hover:border-[#CDA259] hover:bg-[#F7FDFF]'
                    }`}
                  >
                    <span>
                      <span className="block text-sm font-semibold text-[#102131]">{slot.date} · {slot.time}</span>
                      <span className="block text-xs text-gray-500">{t('contact.onlineConsultation')}</span>
                    </span>
                    <CalendarDays size={18} className="text-[#CDA259]" />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-5" data-testid="no-dates-message">
              <p className="text-sm text-gray-600">{t('contact.noDates')}</p>
              <Button
                type="button"
                data-testid="continue-without-date-btn"
                onClick={() => setDateDialogOpen(false)}
                className="w-full bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold"
              >
                {t('contact.continueAnyway')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contact;
